import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, ArrowRight, Navigation, BatteryCharging } from "lucide-react";
import { areas as fallbackAreas, services } from "@/lib/site-config";
import { localAreas } from "@/routes/areas.local.$area";
import { CTABlock } from "@/components/CTABlock";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { listAreas } from "@/lib/cms.functions";
import { CmsSections } from "@/components/CmsSections";

type AreaRow = { slug: string; name: string; description: string | null; sort_order: number };

export const Route = createFileRoute("/areas")({
  loader: async () => {
    const [cfg, areasRes] = await Promise.all([
      getPublicConfig().catch(() => null),
      listAreas().catch(() => ({ items: [] as AreaRow[] })),
    ]);
    const dbAreas = (areasRes.items as AreaRow[]) ?? [];
    const merged = dbAreas.length
      ? dbAreas.map((r) => ({
          slug: r.slug,
          name: r.name,
          description: r.description ?? fallbackAreas.find((a) => a.slug === r.slug)?.description ?? "",
        }))
      : fallbackAreas;
    return { compliance: cfg?.compliance || null, areas: merged };
  },
  head: ({ loaderData }) => seoHead({
    path: "/areas",
    title: "Areas We Cover | East London Garage | Woodseer Autos",
    description: "Woodseer Autos serves drivers across East London — Poplar, Canary Wharf, Isle of Dogs, Limehouse, Docklands and Tower Hamlets. MOT, servicing & repairs.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Areas", path: "/areas" },
    ],
    jsonLd: [localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance))],
  }),
  component: AreasPage,
});

function AreasPage() {
  const { areas } = Route.useLoaderData();
  return (
    <>
      <section className="py-12 sm:py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight">Areas We Cover</h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Based in East London, we serve drivers across Poplar, Canary Wharf, Isle of Dogs, Limehouse, Docklands and Tower Hamlets.
          </p>
        </div>
      </section>

      <CmsSections pageSlug="areas" />
      <section className="pb-12 sm:pb-16 md:pb-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {areas.map((area: { slug: string; name: string; description: string }) => (
              <article
                key={area.slug}
                className="group flex flex-col rounded-xl border border-border bg-card p-6 hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30 transition-all"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
                  <h2 className="text-xl font-semibold text-foreground">
                    <Link
                      to="/areas/$area"
                      params={{ area: area.slug }}
                      className="rounded-sm outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    >
                      {area.name}
                    </Link>
                  </h2>
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{area.description}</p>
                <div className="mt-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Services available:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {services.map((s) => (
                      <span key={s.slug} className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded">{s.name}</span>
                    ))}
                    <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-semibold">
                      <BatteryCharging className="h-3 w-3" aria-hidden="true" /> EV &amp; Hybrid
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <Link
                    to="/areas/$area"
                    params={{ area: area.slug }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary rounded-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    aria-label={`View services in ${area.name}`}
                  >
                    View services in {area.name}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/services/ev-hybrid-servicing"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary rounded-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    aria-label={`EV & Hybrid Servicing for drivers in ${area.name}`}
                  >
                    <BatteryCharging className="h-4 w-4" aria-hidden="true" />
                    EV &amp; Hybrid Servicing
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Local landing pages — deeper, area-specific copy with embedded directions */}
      <section className="pb-16 md:pb-20 bg-secondary/40 pt-12 border-t border-border">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Local landing pages</p>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold text-foreground tracking-tight">Area-specific guides with directions to the yard entrance</h2>
            <p className="mt-3 text-sm text-muted-foreground">Tailored copy, local FAQs and route help for each neighbourhood we serve.</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {localAreas.map((a) => (
              <Link
                key={a.slug}
                to="/areas/local/$area"
                params={{ area: a.slug }}
                className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Navigation className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-foreground">{a.name} <span className="text-muted-foreground font-medium">{a.postcode}</span></p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2">{a.travelTime}</p>
                </div>
                <ArrowRight aria-hidden="true" className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0 mt-2" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 border-t border-border">
        <div className="mx-auto max-w-5xl px-4">
          <Link
            to="/services/ev-hybrid-servicing"
            className="group flex items-start gap-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 via-card to-card p-5 md:p-6 hover:border-primary/60 transition-all"
          >
            <div className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <BatteryCharging className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary">New guide</p>
              <h3 className="mt-1 text-base md:text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                EV &amp; Hybrid servicing across East London
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Routine servicing for Tesla, Polestar, Toyota Hybrid, Kia EV, PHEVs and more — honest about high-voltage work.
              </p>
            </div>
            <ArrowRight aria-hidden="true" className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform shrink-0 mt-1" />
          </Link>
        </div>
      </section>

      <CTABlock headline="Need a Garage Near You?" subtext="We're just a short drive from most of East London. Give us a call." />
    </>
  );
}
